import { useEffect, useRef } from 'react';

// モーダル/オーバーレイを「ブラウザの戻る・戻るジェスチャー」で閉じるための共有機構。
//
// 仕組み（既存のチャット画像モーダルと同じ実績パターンを一般化）:
//  - 開いている間、同一URLのダミー履歴エントリを1つ積む（pushState）。
//  - 戻る操作＝popstate で最前面の層の onClose を呼ぶ（＝モーダルを閉じる。ページ遷移はしない）。
//  - ボタン/Esc/背景などプログラム的に閉じたら、積んだエントリを history.back() で消費して整合を保つ。
//  - 入れ子モーダルはスタックで管理し、常に最前面から閉じる（LIFO）。
//
// ページ側にも独自の popstate ハンドラ（チャットの mobile list⇔chat 遷移など）が存在するため、
// このリスナーは capture フェーズで登録し、「モーダル層が戻るを処理したとき」だけ
// stopImmediatePropagation() でページ側／react-router の popstate ハンドラへの波及を止める。
// 閉じる層が無いときは何もせず、ページ側ハンドラに委ねる。
// ダミーエントリは同一URL＋既存 history.state を保持して push するため、react-router の
// location/idx は不変で、波及を止めても整合が保たれる。

// Entries remain until their owned browser history is consumed. Inactive parents
// are retained below a child so closing out of order cannot leave phantom entries.
const layerStack = [];
let pendingBack = null;
let nextLayerId = 0;
let listenerAttached = false;
let reconcileScheduled = false;
const handledPopEvents = new WeakSet();

export function shouldIgnorePopStateForHistoryDismiss(event) {
  if (event && handledPopEvents.has(event)) return true;
  return pendingBack !== null || layerStack.some((layer) => layer.pushed);
}

function consume(event) {
  handledPopEvents.add(event);
  event.stopImmediatePropagation();
}

function ownsCurrentEntry(layer) {
  return window.history.state?.__dismissLayer === layer.id && window.location.href === layer.url;
}

function finishLayer(layer) {
  const index = layerStack.indexOf(layer);
  if (index !== -1) layerStack.splice(index, 1);
  layer.finished = true;
  layer.onDismissed();
}

function reconcile() {
  if (pendingBack) return;
  // Drain closed entries before pushing a newly opened dialog. history.back is
  // asynchronous: pushing during its traversal would discard the new entry.
  const pushed = layerStack.filter((layer) => layer.pushed);
  const top = pushed[pushed.length - 1];
  if (top && !top.active) {
    if (ownsCurrentEntry(top)) {
      pendingBack = top;
      try {
        window.history.back();
        return;
      } catch {
        pendingBack = null;
      }
    }
    // Navigation or a failed push must never authorize a back on somebody
    // else's entry. Release only our bookkeeping in that case.
    finishLayer(top);
    reconcile();
    return;
  }
  for (const layer of [...layerStack]) {
    if (layer.pushed) continue;
    if (!layer.active) {
      finishLayer(layer);
      continue;
    }
    if (layer.registrationFailed) continue;
    const base = window.history.state;
    layer.baseState = base;
    layer.url = window.location.href;
    try {
      window.history.pushState({ ...(base && typeof base === 'object' ? base : {}), __dismissLayer: layer.id }, '');
      layer.pushed = true;
    } catch {
      // No owned entry: dismissing this dialog must not call history.back.
      layer.registrationFailed = true;
    }
  }
}

function scheduleReconcile() {
  if (reconcileScheduled) return;
  reconcileScheduled = true;
  Promise.resolve().then(() => {
    reconcileScheduled = false;
    reconcile();
  });
}

function isReturnToBase(layer, event) {
  return window.location.href === layer.url &&
    event.state?.__dismissLayer === layer.baseState?.__dismissLayer &&
    event.state?.key === layer.baseState?.key;
}

function handlePopState(event) {
  const top = pendingBack || [...layerStack].reverse().find((layer) => layer.pushed);
  if (!top) return;
  if (!isReturnToBase(top, event)) {
    // A genuine route traversal is not a modal dismissal. Do not swallow it.
    pendingBack = null;
    layerStack.splice(0).forEach((layer) => { layer.finished = true; });
    return;
  }
  consume(event);
  const selfPop = pendingBack === top;
  pendingBack = null;
  if (!selfPop && top.active) top.onClose();
  finishLayer(top);
  reconcile();
}

function ensureListener() {
  if (listenerAttached || typeof window === 'undefined') return;
  window.addEventListener('popstate', handlePopState, true);
  listenerAttached = true;
}
ensureListener();

/**
 * @param {boolean} active 開いている（かつ戻るで閉じてよい）とき true。
 * @param {() => void} onClose 戻る操作で呼ぶクローズ関数。
 * @param {(() => void)=} onDismissed 所有する履歴の消費後の画面遷移に使う。
 */
export default function useHistoryDismiss(active, onClose, onDismissed) {
  const onCloseRef = useRef(onClose);
  const onDismissedRef = useRef(onDismissed);
  const layerRef = useRef(null);
  onCloseRef.current = onClose;
  onDismissedRef.current = onDismissed;

  useEffect(() => {
    if (!active) return undefined;
    ensureListener();
    let layer = layerRef.current;
    if (!layer || layer.finished || pendingBack === layer) {
      layer = {
        id: `modal-${++nextLayerId}`, active: true, pushed: false, finished: false,
        onClose: () => onCloseRef.current?.(),
        onDismissed: () => onDismissedRef.current?.(),
      };
      layerRef.current = layer;
      layerStack.push(layer);
    }
    layer.active = true;
    if (layerStack.every((entry) => entry.active)) reconcile();
    else scheduleReconcile();
    return () => {
      layer.active = false;
      // StrictMode immediately reacquires the same layer. Reconcile only after
      // that lifecycle turn, before starting any irreversible browser traversal.
      scheduleReconcile();
    };
  }, [active]);
}
